/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoSwmProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoSwmProps {
    /**
     * A string representing the CSS class to be applied to the LogoSwmIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#52AE32'
     */
    color1?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#005297'
     */
    color2?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#000'
     */
    color3?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color4?: string;
    /**
     * A number or string representing the height of the LogoSwmIcon element.
     *
     * @default 23
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LogoSwm'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoSwmIcon element.
     *
     * @default 67
     */
    width?: number | string;
}

/**
 * The `LogoSwm` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoSwmProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoSwmIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.color2    A string representing the color to be applied to the Icon.
 * @param props.color3    A string representing the color to be applied to the Icon.
 * @param props.color4    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoSwmIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoSwmIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoSwmComponent = <LogoSwm className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoSwm = ({
    className = '',
    color1 = '#52AE32',
    color2 = '#005297',
    color3 = '#000',
    color4 = '#fff',
    height = 23,
    ref,
    testId = 'LogoSwm',
    width = 67
}: LogoSwmProps) => (
    <svg
        ref={ref}
        aria-label="SWM Logo"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 67 23"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <g clipPath="url(#a)">
            <path d="m48.56.5-12.13 22h4.31L52.85.5z" fill={color1} />
            <path d="m41.47.5-12.12 22h4.3L45.78.5z" fill={color2} />
            <path d="M15.32 3.87V19.1h3.94l5.09-8.8v8.8h4.06l8.45-15.24H33.1l-4.73 8.27V3.87h-4.3l-4.8 8.1v-8.1zm30.09 15.24h3.83l4.68-8.15v8.15h4.1l5-8.13v8.13H67V3.87h-4l-4.98 8.8v-8.8h-4.2zM4.07 13.9a2.66 2.66 0 0 0 2.68 2.6c1.47 0 2.67-.75 2.67-1.66 0-.52-.3-1.16-1.5-1.47l-1.47-.44c-.82-.24-1.3-.3-2.11-.59-.54-.18-.83-.32-1.36-.53C1.34 10.94.42 9.5.52 7.9.38 5.24 3.34 3.23 6.81 3.23s6.28 2.09 6.28 4.67v.42h-4.1l.01-.3c0-.85-.98-1.54-2.2-1.54h-.26c-1.07 0-1.93.49-1.93 1.08v.17c0 .43.38.83.95 1.06.65.26.98.34 1.65.53.75.21 1.18.31 1.91.56.79.26 1.98.75 1.98.75 1.35.59 2.42 1.97 2.42 3.28.19 3.45-2.21 5.55-6.76 5.55C2.23 19.46 0 17.41 0 13.9z" fill={color3} />
        </g>
        <defs>
            <clipPath id="a">
                <path d="M0 .5h67.01v22H0z" fill={color4} />
            </clipPath>
        </defs>
    </svg>
);

LogoSwm.displayName = 'LogoSwm';